import React, { useEffect, useState, useRef } from 'react';
import { apiUrl } from './apiUrl';
import { toast } from '@ohif/ui-next';
import { DicomMetadataStore } from '@ohif/core';

const localStr = localStorage.getItem('usuario');
const session = localStr ? JSON.parse(localStr) : null;
const rol = session && session.user.rol ? session.user.rol : '';
const correo = session && session.user.correo ? session.user.correo : '';

function UploadDocuments({
  value = '',
  seriesStudy = '',
  seriesColumn = '',
  temporalView = false,
  instanceUid = '',
}: {
  value: string;
  seriesStudy: any;
  seriesColumn: any;
  temporalView: boolean;
  instanceUid?: string;
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [fileName, setFileName] = useState('');
  const [activeTab, setActiveTab] = useState('subir');
  const [fileContent, setFileContent] = useState('');
  const [fileURL, setFileURL] = useState(null);
  const [paciente, setPaciente] = useState('');

  const [fecha, setFecha] = useState('');
  const [descripcion, setDescripcion] = useState('');
  const [plantilla, setPlantilla] = useState('');
  const [conclusion, setConclucion] = useState('');
  const [firma, setFirma] = useState('');
  const [campoActivo, setCampoActivo] = useState('');
  const [spinner, setSpinner] = useState(false);
  const [loading, setLoading] = useState(true);
  const [instanceMeta, setInstanceMeta] = useState<any>(null);

  const [escuchando, setEscuchando] = useState(false);
  const [escuchandoDescripcion, setEscuchandoDescripcion] = useState(false);
  const [escuchandoConclusion, setEscuchandoConclusion] = useState(false);

  const recognitionRef = useRef(null);
  const [vistaUsuario, setVistaUsuario] = useState('');
  const [rolUsuario, setRolUsuario] = useState('');

  useEffect(() => {
    const localStr = localStorage.getItem('usuario');
    const session = localStr ? JSON.parse(localStr) : null;
    const tiposVista = session ? session.user.vistas : [];
    const tiposRol = session ? session.user.role : '';
    setRolUsuario(tiposRol);
    setVistaUsuario(tiposVista);
  }, []);
  const fetchStudyMeta = async (attempts = 0) => {
    try {
      const studyMeta = await DicomMetadataStore.getStudy(String(instanceUid));

      if (studyMeta?.series?.length) {
        const instance = studyMeta.series[0].instances[0];
        setInstanceMeta(instance);
      } else {
        setInstanceMeta(null);
      }
    } catch (error) {
      console.error('Error fetching study data:', error);
      setInstanceMeta(null);
    } finally {
      setLoading(false);
      if (!instanceMeta && attempts < 5) {
        console.log(`Retrying... Attempt ${attempts + 1}`);
        setTimeout(() => fetchStudyMeta(attempts + 1), 1000);
      }
    }
  };

  useEffect(() => {
    if (instanceUid) {
      fetchStudyMeta();
    }
  }, [instanceUid]);

  const instanceData = instanceMeta
    ? {
      StudyDate: instanceMeta.StudyDate || '',
      InstitutionName: instanceMeta.InstitutionName || '',
      ReferringPhysicianName: instanceMeta.ReferringPhysicianName || '',
      StationName: instanceMeta.StationName || '',
      PerformingPhysicianName: instanceMeta.PerformingPhysicianName || '',
      NameOfPhysiciansReadingStudy: instanceMeta.NameOfPhysiciansReadingStudy || '',
      OperatorsName: instanceMeta.OperatorsName || '',
      ManufacturerModelName: instanceMeta.ManufacturerModelName || '',
      PatientName: instanceMeta.PatientName ? instanceMeta.PatientName[0].Alphabetic : null,
      ProtocolName: instanceMeta.ProtocolName || '',
    }
    : {};
  const normalizePatientName = (rawName = '') => {
    return rawName
      .normalize('NFD') // Separa letras de tildes
      .replace(/[\u0300-\u036f]/g, '') // Elimina tildes (Á → A)
      .replace(/[\^,]/g, ' ') // Reemplaza ^ y , por espacio
      .replace(/\s+/g, ' ') // Colapsa espacios múltiples
      .trim(); // Quita espacios al inicio y fin
  };
  useEffect(() => {
    // Uso en tu código
    const normalizedPatientName = normalizePatientName(instanceData.PatientName);
    setPaciente(normalizedPatientName);

    const verificarArchivo = async () => {
      try {
        const response = await fetch(`${apiUrl}/api/subida/upload/${paciente}`);
        if (response.status === 200) {
          setReporteDisponible(true);
        }
      } catch (error) {
        setReporteDisponible(false);
      }
    };

    if ((isOpen || temporalView) && paciente) {
      verificarArchivo();
    }
  }, [paciente, apiUrl, isOpen, temporalView, instanceData.PatientName]);

  useEffect(() => {
    if (!isOpen) {
      if (fileURL) {
        URL.revokeObjectURL(fileURL);
        setFileURL(null);
      }

      setFileContent('');
      setFileName('');
    }
  }, [isOpen]);

  const iframeRef = useRef<HTMLIFrameElement | null>(null);

  const iniciarDictado = campo => {
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SpeechRecognition) {
      toast.warning('Tu navegador no soporta la API de reconocimiento de voz.');
      return;
    }

    const recognition = new SpeechRecognition();
    recognition.lang = 'es-ES';
    recognition.interimResults = false;
    recognition.continuous = false;

    recognition.onresult = event => {
      const resultado = event.results[0][0].transcript.trim();
      if (campo === 'descripcion') {
        setDescripcion(prev => prev + ' ' + resultado);
      } else if (campo === 'conclusion') {
        setConclucion(prev => prev + ' ' + resultado);
      }
    };

    recognition.onerror = event => {
      console.error('Error de reconocimiento:', event.error);
      setEscuchandoDescripcion(false);
      setEscuchandoConclusion(false);
    };

    recognition.onend = () => {
      setEscuchandoDescripcion(false);
      setEscuchandoConclusion(false);
    };

    recognition.start();
    setCampoActivo(campo);
    if (campo === 'descripcion') {
      setEscuchandoDescripcion(true);
    } else if (campo === 'conclusion') {
      setEscuchandoConclusion(true);
    }

    recognitionRef.current = recognition;
  };

  const detenerDictado = () => {
    if (recognitionRef.current) {
      recognitionRef.current.stop();
      setEscuchandoDescripcion(false);
      setEscuchandoConclusion(false);
    }
  };

  const [reporteDisponible, setReporteDisponible] = useState(false);
  const [reporteDisponibleDocx, setReporteDisponibleDocx] = useState(false);

  const handleFileChangeFirma = e => {
    if (e.target.id !== 'file_input') {
      return;
    }

    const file = e.target.files[0];
    if (!file) {
      return;
    }

    const img = new Image();
    const reader = new FileReader();

    reader.onload = function (event) {
      img.src = event.target.result;
    };

    img.onload = function () {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      const width = 300;
      const height = 100;
      canvas.width = width;
      canvas.height = height;
      ctx.drawImage(img, 0, 0, width, height);
      const resizedBase64 = canvas.toDataURL('image/png');
      setFirma(resizedBase64);
    };

    reader.readAsDataURL(file);
  };

  const openModal = () => {
    setIsOpen(true);
  };

  const closeModal = () => {
    if (fileURL) {
      URL.revokeObjectURL(fileURL);
    }
    setFileName('');
    setFileURL(null);
    setFileContent('');
    setIsOpen(false);
    setActiveTab('subir');
  };

  const handleUpload = async () => {
    const input = document.getElementById('file_input') as HTMLInputElement;
    if (!input.files || input.files.length === 0) {
      toast.warning('Por favor, selecciona un archivo para subir');
      return;
    }

    if (reporteDisponible) {
      toast.warning('Ya existe un archivo subido para este paciente', {
        action: {
          label: 'Cambiar archivo',
          onClick: async () => {
            const password = prompt('Introduce tu contraseña para hacer el cambio');
            if (!password) {
              return toast.error('Debes ingresar una contraseña');
            }

            try {
              const response = await fetch(apiUrl + '/api/auth/login', {
                method: 'POST',
                headers: {
                  'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                  correo,
                  contrasena: password,
                }),
              });

              if (!response.ok) {
                toast.error('Contraseña incorrecta');
                return;
              }

              await subirArchivo(input.files[0]);
            } catch (error) {
              console.error('Error en autenticación:', error);
              toast.error('Error al verificar la contraseña');
            }
          },
        },
      });
      return;
    }

    await subirArchivo(input.files[0]);
  };

  const subirArchivo = async (file: File) => {
    setSpinner(true);
    const formData = new FormData();
    formData.append('file', file);
    formData.append('paciente', normalizePatientName(paciente));

    try {
      const response = await fetch(apiUrl + '/api/subida/upload', {
        method: 'POST',
        body: formData,
      });

      const result = await response.json();
      toast.success(result.message || 'Archivo subido con éxito');
      setIsOpen(false);
    } catch (error) {
      toast.warning('Error al subir el archivo');
      console.error(error);
    } finally {
      setSpinner(false);
    }
  };

  useEffect(() => {
    const hoy = new Date();
    const fechaActual = hoy.toISOString().split('T')[0];
    setFecha(fechaActual);
  }, []);

  const handleUploadDocument = async e => {
    e.preventDefault();
    setSpinner(true);

    const valueDicom = seriesStudy.map((row: any) => {
      const rowData = {};
      Object.keys(row).forEach(key => {
        const columnName = seriesColumn[key] || key;
        rowData[columnName] = row[key];
      });
      return rowData;
    });

    if (!paciente || !fecha || !descripcion || !conclusion) {
      toast.warning('Por favor, completa todos los campos requeridos');
      setSpinner(false);
      return;
    }

    const requestBody = {
      paciente,
      fecha: fecha || instanceData.StudyDate || '',
      descripcion,
      plantilla: plantilla || instanceData.ProtocolName,
      conclusion,
      firma,
      estacion: instanceData.StationName,
      medico: instanceMeta.NameOfPhysiciansReadingStudy,
      valueDicom,
    };
    try {
      const response = await fetch(apiUrl + '/api/documentos/generate-doc', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestBody),
      });

      if (!response.ok) {
        throw new Error('Error en la respuesta del servidor');
      }

      const result = await response.json();
      toast.success(result.message || 'Documento generado con éxito');
    } catch (error) {
      toast.warning('Error al generar el documento: ' + error.message);
    } finally {
      setSpinner(false);
      setIsOpen(false);
    }
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files[0];

    if (fileURL) {
      URL.revokeObjectURL(fileURL);
      setFileURL(null);
    }

    setFileName(file ? file.name : '');
    setFileContent('');

    if (!file) {
      return;
    }

    const fileType = file.type;

    if (fileType.startsWith('image/') || fileType === 'application/pdf') {
      const url = URL.createObjectURL(file);
      setFileURL(url);
    } else if (
      fileType.startsWith('text/') ||
      file.name.endsWith('.json') ||
      file.name.endsWith('.csv')
    ) {
      const reader = new FileReader();
      reader.onload = e => {
        setFileContent(e.target?.result as string);
      };
      reader.readAsText(file);
    } else {
      setFileContent('Previsualización no disponible para este tipo de archivo.');
    }
  };

  if (!instanceMeta) {
    return null;
  }

  return (
    <>
      {!temporalView ? (
        <button
          onClick={openModal}
          className={
            `${reporteDisponible ? 'bg-yellow-600 hover:bg-yellow-700 ' : 'bg-primary-main hover:bg-primary-light '}` +
            'flex w-full items-center justify-center gap-2 rounded p-2 text-white sm:justify-between'
          }
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="16"
            height="16"
            fill="currentColor"
            className="bi bi-file-arrow-up hover:text-greene h-5 w-5 text-white"
            viewBox="0 0 16 16"
          >
            <path d="M8 11a.5.5 0 0 0 .5-.5V6.707l1.146 1.147a.5.5 0 0 0 .708-.708l-2-2a.5.5 0 0 0-.708 0l-2 2a.5.5 0 1 0 .708.708L7.5 6.707V10.5a.5.5 0 0 0 .5.5" />
            <path d="M4 0a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h8a2 2 0 0 0 2-2V2a2 2 0 0 0-2-2zm0 1h8a1 1 0 0 1 1 1v12a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V2a1 1 0 0 1 1-1" />
          </svg>
          <p className="">Reporte</p>
        </button>
      ) : (
        <div className="">
          {reporteDisponible ? (
            <a
              className="bg-yellow-600 hover:bg-yellow-700 flex w-full items-center justify-center gap-2 rounded p-2 text-white sm:justify-between"
              href={`${apiUrl}/api/subida/upload/${encodeURIComponent(paciente)}`}
              target="_blank"
              rel="noopener noreferrer"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="16"
                height="16"
                fill="currentColor"
                className="bi bi-eye-fill p-auto m-auto ml-1"
                viewBox="0 0 16 16"
              >
                <path d="M10.5 8a2.5 2.5 0 1 1-5 0 2.5 2.5 0 0 1 5 0" />
                <path d="M0 8s3-5.5 8-5.5S16 8 16 8s-3 5.5-8 5.5S0 8 0 8m8 3.5a3.5 3.5 0 1 0 0-7 3.5 3.5 0 0 0 0 7" />
              </svg>
              Ver reporte
            </a>
          ) : (
            <button
              className="mt-3 flex cursor-not-allowed rounded bg-gray-600 p-2 text-white"
              disabled
            >
              Reporte no disponible
            </button>
          )}
        </div>
      )}

      {isOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-80 text-white">
          <div className="bg-primary-dark max-h-[90vh] w-full max-w-md overflow-y-auto rounded-lg p-6 pt-1 shadow-lg">
            {/* Tabs */}
            {rolUsuario === 'admin' && (
              <div className="mb-4 text-center text-sm font-medium text-gray-200">
                <ul className="-mb-px flex flex-wrap justify-center">
                  <li className="me-2">
                    <button
                      onClick={() => setActiveTab('subir')}
                      className={`inline-block rounded-t-lg border-b-2 p-4 ${activeTab === 'subir'
                        ? 'border-blue-300 text-blue-300'
                        : 'border-transparent hover:border-blue-600 hover:text-blue-400'
                        }`}
                    >
                      Subir
                    </button>
                  </li>
                  <li className="me-2">
                    <button
                      onClick={() => setActiveTab('plantilla')}
                      className={`inline-block rounded-t-lg border-b-2 p-4 ${activeTab === 'plantilla'
                        ? 'border-blue-300 text-blue-300'
                        : 'border-transparent hover:border-blue-600 hover:text-blue-400'
                        }`}
                    >
                      Desde plantilla
                    </button>
                  </li>
                </ul>
              </div>
            )}

            {/* Subir archivo */}
            {activeTab === 'subir' && (
              <div id="subida de archivo">
                {rolUsuario === 'admin' && (
                  <>
                    <input
                      className="bg-black"
                      type="text"
                      hidden
                      name="paciente"
                      id="paciente"
                      value={paciente}
                      onChange={e => setPaciente(e.target.value)}
                    />
                    <input
                      autoFocus
                      onChange={handleFileChange}
                      className="border-primary-light placeholder-primary-light block w-full cursor-pointer rounded-lg bg-black p-3 text-sm text-gray-200 focus:outline-none"
                      id="file_input"
                      type="file"
                    />
                    {fileName && (
                      <div className="mt-2">
                        <p className="text-sm text-gray-300">
                          Archivo seleccionado: <span className="font-medium">{fileName}</span>
                        </p>

                        {fileURL && (
                          <>
                            {fileURL.includes('.pdf') ? (
                              <iframe
                                ref={iframeRef}
                                src={fileURL}
                                title="Vista previa PDF"
                                className="mt-4 h-64 w-full rounded bg-white"
                              />
                            ) : (
                              <img
                                src={fileURL}
                                alt="Vista previa"
                                className="mt-4 max-h-64 w-auto rounded object-contain"
                              />
                            )}
                          </>
                        )}

                        {fileContent && (
                          <div className="mt-4 max-h-60 overflow-y-auto rounded bg-gray-800 p-3 text-sm text-white">
                            <pre>{fileContent}</pre>
                          </div>
                        )}

                        {!fileContent && !fileURL && (
                          <p className="mt-4 text-sm text-gray-400">
                            No se puede mostrar una vista previa para este tipo de archivo.
                          </p>
                        )}
                      </div>
                    )}
                  </>
                )}
                <div className="flex w-full items-center justify-center">
                  {reporteDisponible ? (
                    <a
                      className="mt-3 flex rounded bg-blue-700 p-2 text-white"
                      href={`${apiUrl}/api/subida/upload/${encodeURIComponent(normalizePatientName(instanceData.PatientName))}`}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Ver reporte
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="16"
                        height="16"
                        fill="currentColor"
                        className="bi bi-eye-fill p-auto m-auto ml-2"
                        viewBox="0 0 16 16"
                      >
                        <path d="M10.5 8a2.5 2.5 0 1 1-5 0 2.5 2.5 0 0 1 5 0" />
                        <path d="M0 8s3-5.5 8-5.5S16 8 16 8s-3 5.5-8 5.5S0 8 0 8m8 3.5a3.5 3.5 0 1 0 0-7 3.5 3.5 0 0 0 0 7" />
                      </svg>
                    </a>
                  ) : (
                    <button
                      className="mt-3 flex cursor-not-allowed rounded bg-gray-600 p-2 text-white"
                      disabled
                    >
                      Reporte no disponible
                    </button>
                  )}
                </div>
                <div className="flex place-items-center items-center justify-between justify-items-center gap-3">
                  <button
                    autoFocus
                    type="button"
                    onClick={closeModal}
                    className="mt-6 w-full rounded bg-red-700 px-4 py-2 text-white transition hover:bg-red-600"
                  >
                    Cerrar
                  </button>
                  {
                    vistaUsuario.includes('INFORMES') && (
                      <button
                        onClick={handleUpload}
                        className="mt-4 flex w-full items-center justify-center gap-x-2 rounded bg-green-600 px-4 py-2 text-white transition hover:bg-green-500"
                      >
                        {spinner && (
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="16"
                            height="16"
                            fill="currentColor"
                            className="bi bi-arrow-clockwise animate-spin"
                            viewBox="0 0 16 16"
                          >
                            <path
                              fillRule="evenodd"
                              d="M8 3a5 5 0 1 0 4.546 2.914.5.5 0 0 1 .908-.417A6 6 0 1 1 8 2z"
                            />
                            <path d="M8 4.466V.534a.25.25 0 0 1 .41-.192l2.36 1.966c.12.1.12.284 0 .384L8.41 4.658A.25.25 0 0 1 8 4.466" />
                          </svg>
                        )}
                        Subir archivo
                      </button>
                    )}
                </div>
              </div>
            )}

            {/* Formulario desde plantilla */}
            {activeTab === 'plantilla' && (
              <div id="formulario">
                <form
                  className="space-y-2"
                  onSubmit={handleUploadDocument}
                >
                  <div>
                    <label
                      className="mb-2 font-bold"
                      htmlFor="paciente"
                    >
                      Paciente
                    </label>
                    <input
                      className="w-full rounded bg-black p-2 text-gray-200 focus:ring-2 focus:ring-green-300"
                      type="text"
                      name="paciente"
                      id="paciente"
                      value={paciente}
                      onChange={e => setPaciente(e.target.value)}
                    />
                  </div>

                  <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                    <div className="hidden">
                      <label
                        className="mb-2 font-bold"
                        htmlFor="plantilla"
                      >
                        Estudio
                      </label>
                      <select
                        name="plantilla"
                        id="plantilla"
                        className="w-full rounded bg-black p-2 text-gray-200 focus:ring-2 focus:ring-green-300"
                        value={plantilla}
                        onChange={e => setPlantilla(e.target.value)}
                      >
                        <option value="">Selecciona un estudio</option>
                        <option value="plantilla1">Rayos X</option>
                        <option value="plantilla2">Mamografia</option>
                        <option value="plantilla3">Tomografia</option>
                      </select>
                    </div>

                    <div className="hidden">
                      <label
                        className="mb-2 font-bold"
                        htmlFor="fecha"
                      >
                        Fecha
                      </label>
                      <input
                        id="fecha"
                        type="date"
                        className="w-full rounded bg-black p-2 text-gray-200 focus:ring-2 focus:ring-green-300"
                        placeholder="Campo 2"
                        value={fecha}
                        onChange={e => setFecha(e.target.value)}
                      />
                    </div>
                  </div>
                  <div>
                    <div className="flex gap-2">
                      <label
                        className="mb-2 px-2 py-1 font-bold"
                        htmlFor="descripcion"
                      >
                        Descripción
                      </label>
                      {!escuchandoDescripcion ? (
                        <button
                          className="mr-2 mb-2 rounded-lg bg-green-600 px-5 py-1 text-sm font-medium text-white hover:bg-green-500"
                          type="button"
                          onClick={() => iniciarDictado('descripcion')}
                          disabled={escuchandoConclusion}
                        >
                          🎤 Iniciar dictado
                        </button>
                      ) : (
                        <button
                          className="mr-2 mb-2 rounded-lg bg-red-700 px-5 py-1 text-sm font-medium text-white hover:bg-red-800"
                          type="button"
                          onClick={detenerDictado}
                        >
                          🛑 Detener dictado
                        </button>
                      )}
                    </div>
                    <textarea
                      id="descripcion"
                      className="w-full rounded bg-black p-2 text-gray-200 focus:ring-2 focus:ring-green-300"
                      placeholder="Campo 3"
                      value={descripcion}
                      onChange={e => setDescripcion(e.target.value)}
                    />
                  </div>
                  <div>
                    <div className="flex gap-2">
                      <label
                        className="mb-2 px-2 py-1 font-bold"
                        htmlFor="conclusion"
                      >
                        Conclucion
                      </label>
                      {!escuchandoConclusion ? (
                        <button
                          className="mr-2 mb-2 rounded-lg bg-green-600 px-5 py-1 text-sm font-medium text-white hover:bg-green-500"
                          type="button"
                          onClick={() => iniciarDictado('conclusion')}
                          disabled={escuchandoDescripcion}
                        >
                          🎤 Iniciar dictado
                        </button>
                      ) : (
                        <button
                          className="mr-2 mb-2 rounded-lg bg-red-700 px-5 py-2.5 text-sm font-medium text-white hover:bg-red-800"
                          type="button"
                          onClick={detenerDictado}
                        >
                          🛑 Detener dictado
                        </button>
                      )}
                    </div>
                    <textarea
                      id="conclusion"
                      className="w-full rounded bg-black p-2 text-gray-200 focus:ring-2 focus:ring-green-300"
                      placeholder="Campo 3"
                      value={conclusion}
                      onChange={e => setConclucion(e.target.value)}
                    />
                  </div>
                  <div className="hidden">
                    <label
                      className="mb-2 font-bold"
                      htmlFor="conclucion"
                    >
                      Firma digitalizada
                    </label>
                    <input
                      id="file_input"
                      type="file"
                      accept="image/*"
                      onChange={handleFileChangeFirma}
                      className="border-primary-light placeholder-primary-light block w-full cursor-pointer rounded-lg bg-black p-3 text-sm text-gray-200 focus:outline-none"
                    />
                    {firma && (
                      <div className="mt-4">
                        <p>Vista previa de firma:</p>
                        <img
                          src={firma}
                          alt="Firma"
                          style={{ border: '1px solid #ccc' }}
                        />
                      </div>
                    )}
                  </div>
                  <div className="flex w-full items-center justify-center">
                    {reporteDisponibleDocx ? (
                      <a
                        className="mt-3 flex rounded bg-blue-700 p-2 text-white"
                        href={apiUrl + '/api/documentos/download/' + paciente + '.docx'}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        Ver reporte
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="16"
                          height="16"
                          fill="currentColor"
                          className="bi bi-eye-fill p-auto m-auto ml-2"
                          viewBox="0 0 16 16"
                        >
                          <path d="M10.5 8a2.5 2.5 0 1 1-5 0 2.5 2.5 0 0 1 5 0" />
                          <path d="M0 8s3-5.5 8-5.5S16 8 16 8s-3 5.5-8 5.5S0 8 0 8m8 3.5a3.5 3.5 0 1 0 0-7 3.5 3.5 0 0 0 0 7" />
                        </svg>
                      </a>
                    ) : (
                      <button
                        className="mt-3 flex cursor-not-allowed rounded bg-gray-600 p-2 text-white"
                        disabled
                      >
                        Reporte no disponible
                      </button>
                    )}
                  </div>

                  <div className="flex justify-between gap-3">
                    <button
                      onClick={closeModal}
                      type="button"
                      className="mt-4 w-full rounded bg-red-700 px-4 py-2 text-white transition hover:bg-red-600"
                    >
                      Cerrar
                    </button>

                    <button
                      type="submit"
                      className="mt-4 flex w-full items-center justify-center gap-x-2 rounded bg-green-600 px-4 py-2 text-white transition hover:bg-green-500"
                    >
                      {spinner && (
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="16"
                          height="16"
                          fill="currentColor"
                          className="bi bi-arrow-clockwise animate-spin"
                          viewBox="0 0 16 16"
                        >
                          <path
                            fillRule="evenodd"
                            d="M8 3a5 5 0 1 0 4.546 2.914.5.5 0 0 1 .908-.417A6 6 0 1 1 8 2z"
                          />
                          <path d="M8 4.466V.534a.25.25 0 0 1 .41-.192l2.36 1.966c.12.1.12.284 0 .384L8.41 4.658A.25.25 0 0 1 8 4.466" />
                        </svg>
                      )}
                      Subir archivos
                    </button>
                  </div>
                </form>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
}

export default UploadDocuments;
// import React, { useEffect, useState } from 'react';
// import { apiUrl } from './apiUrl';
// import { DicomMetadataStore } from '@ohif/core';

// interface UploadDocumentsProps {
//   instanceUid?: string;
// }

// const UploadDocuments: React.FC<UploadDocumentsProps> = ({
//   instanceUid = '',
// }) => {
//   const [paciente, setPaciente] = useState('');
//   const [instanceMeta, setInstanceMeta] = useState<any>(null);
//   const [reporteDisponible, setReporteDisponible] = useState(false);

//
//   const formatearNombre = (dicomName: string) => {
//     if (!dicomName) {
//       return '';
//     }
//     const [apellidoParte, nombreParte] = dicomName.split('^');
//     return nombreParte ? `${apellidoParte.trim()}, ${nombreParte.trim()}` : apellidoParte.trim();
//   };

//
//   const fetchStudyMeta = async (attempts = 0) => {
//     try {
//       const studyMeta = await DicomMetadataStore.getStudy(String(instanceUid));
//       if (studyMeta?.series?.length) {
//         const instance = studyMeta.series[0].instances[0];
//         setInstanceMeta(instance);
//       } else {
//         setInstanceMeta(null);
//       }
//     } catch (error) {
//       console.error('Error fetching study data:', error);
//       setInstanceMeta(null);
//       if (attempts < 5) {
//         setTimeout(() => fetchStudyMeta(attempts + 1), 1000);
//       }
//     }
//   };

//
//   useEffect(() => {
//     if (instanceUid) {
//       fetchStudyMeta();
//     }
//   }, [instanceUid]);

//
//   useEffect(() => {
//     if (instanceMeta?.PatientName?.[0]?.Alphabetic) {
//       const nombre = formatearNombre(instanceMeta.PatientName[0].Alphabetic);
//       setPaciente(nombre);
//     }
//   }, [instanceMeta]);

//
//   useEffect(() => {
//     const verificarArchivo = async () => {
//       if (!paciente) {
//         return;
//       }

//       try {
//         const response = await fetch(`${apiUrl}/api/subida/upload/${paciente}`);
//         setReporteDisponible(response.ok);
//       } catch {
//         setReporteDisponible(false);
//       }
//     };

//     verificarArchivo();
//   }, [paciente]);

//   if (!instanceMeta || !reporteDisponible) {
//     return null;
//   }

//   return (
//     <a
//       href={`${apiUrl}/api/subida/upload/${paciente}`}
//       target="_blank"
//       rel="noopener noreferrer"
//       className="flex w-full items-center justify-center gap-2 rounded bg-yellow-600 p-2 text-white hover:bg-yellow-700 sm:justify-between"
//     >
//       <svg
//         xmlns="http://www.w3.org/2000/svg"
//         width="16"
//         height="16"
//         fill="currentColor"
//         className="bi bi-file-arrow-up hover:text-greene h-5 w-5 text-white"
//         viewBox="0 0 16 16"
//       >
//         <path d="M8 11a.5.5 0 0 0 .5-.5V6.707l1.146 1.147a.5.5 0 0 0 .708-.708l-2-2a.5.5 0 0 0-.708 0l-2 2a.5.5 0 1 0 .708.708L7.5 6.707V10.5a.5.5 0 0 0 .5.5" />
//         <path d="M4 0a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h8a2 2 0 0 0 2-2V2a2 2 0 0 0-2-2zm0 1h8a1 1 0 0 1 1 1v12a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V2a1 1 0 0 1 1-1" />
//       </svg>
//       <p>Reporte</p>
//     </a>
//   );
// };

// export default UploadDocuments;
